/**
 * A debounced callback with an imperative `flush` and `cancel`.
 *
 * The editor needs both (`061-editor.md` §8): blur, tab-hide, `Mod+S` and a route change
 * must all be able to say "stop waiting, save NOW", and the conflict path must be able to
 * cancel an armed save that is about to clobber something.
 *
 * The callback is held in a ref so that changing it does not restart the timer — a
 * debounce that resets on every render is not a debounce.
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';

export interface DebouncedCallback<A extends unknown[]> {
  (...args: A): void;
  /** Run the pending call immediately, if one is armed. */
  flush: () => void;
  cancel: () => void;
  pending: () => boolean;
}

export function useDebouncedCallback<A extends unknown[]>(
  callback: (...args: A) => void,
  delay: number,
): DebouncedCallback<A> {
  const latest = useRef(callback);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const args = useRef<A | null>(null);

  useEffect(() => {
    latest.current = callback;
  }, [callback]);

  const cancel = useCallback(() => {
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    args.current = null;
  }, []);

  const flush = useCallback(() => {
    if (timer.current === null || args.current === null) return;
    const pending = args.current;
    cancel();
    latest.current(...pending);
  }, [cancel]);

  // Unmounting must not silently drop a pending save: the user navigated away from a page
  // they had just typed into, and the whole point of this app is that that is safe.
  useEffect(
    () => () => {
      if (timer.current !== null && args.current !== null) {
        const pending = args.current;
        clearTimeout(timer.current);
        timer.current = null;
        args.current = null;
        latest.current(...pending);
      }
    },
    [],
  );

  return useMemo(() => {
    const debounced = ((...next: A) => {
      args.current = next;
      if (timer.current !== null) clearTimeout(timer.current);

      timer.current = setTimeout(() => {
        const pending = args.current;
        timer.current = null;
        args.current = null;
        if (pending !== null) latest.current(...pending);
      }, delay);
    }) as DebouncedCallback<A>;

    debounced.flush = flush;
    debounced.cancel = cancel;
    debounced.pending = () => timer.current !== null;

    return debounced;
  }, [cancel, delay, flush]);
}
